import { EduChallenge } from "../types";

export interface EduTemplate {
  templateId: string;
  title: string;
  emoji: string;
  description: string;
  subject: string;
  gradeRange: string;
  durationMinutes: number;
  challenges: EduChallenge[];
}

export const FIND_SOMEONE_WHO: EduTemplate = {
  templateId: "find-someone-who",
  title: "Find Someone Who",
  emoji: "🤝",
  description:
    "Get to know your classmates by finding someone who matches each prompt.",
  subject: "Icebreaker",
  gradeRange: "All",
  durationMinutes: 30,
  challenges: [
    { text: "Photograph someone who has a pet", hint: null, points: 100 },
    { text: "Find someone who speaks more than one language", hint: null, points: 100 },
    { text: "Photograph someone wearing the same color as you", hint: null, points: 100 },
    { text: "Find someone who has the same birthday month", hint: null, points: 100 },
    { text: "Photograph someone who likes the same music as you", hint: null, points: 100 },
  ],
};

export const CAMPUS_EXPLORER: EduTemplate = {
  templateId: "campus-explorer",
  title: "Campus Explorer",
  emoji: "🏫",
  description:
    "Help students learn the school by photographing key campus locations.",
  subject: "Orientation",
  gradeRange: "All",
  durationMinutes: 45,
  challenges: [
    { text: "Photograph the main entrance to the school", hint: "Look for the front office sign", points: 100 },
    { text: "Photograph the library", hint: null, points: 100 },
    { text: "Photograph the cafeteria or lunchroom", hint: null, points: 100 },
    { text: "Photograph the gym or fitness area", hint: null, points: 100 },
    { text: "Photograph a place where you can read quietly", hint: null, points: 100 },
    { text: "Photograph the nurse's office or first-aid station", hint: null, points: 100 },
    { text: "Photograph a piece of school art or a mural", hint: "Hallways often have student work", points: 100 },
    { text: "Photograph a sign that helps a new student find their way", hint: null, points: 100 },
  ],
};

export const SCIENCE_IN_THE_WILD: EduTemplate = {
  templateId: "science-in-the-wild",
  title: "Science in the Wild",
  emoji: "🔬",
  description:
    "Spot scientific concepts in the real world, from symmetry to states of matter.",
  subject: "Science",
  gradeRange: "3-8",
  durationMinutes: 45,
  challenges: [
    { text: "Photograph an example of symmetry in nature", hint: "Leaves, butterflies, flowers", points: 100 },
    { text: "Photograph evidence of erosion", hint: "Worn rocks, washed-out paths", points: 100 },
    { text: "Photograph a habitat where an animal lives", hint: null, points: 100 },
    { text: "Photograph water in two different states", hint: "Ice and liquid both count", points: 150 },
    { text: "Photograph a simple machine in use", hint: "Lever, wheel, pulley, ramp", points: 100 },
    { text: "Photograph a shadow that shows the sun's position", hint: null, points: 100 },
    { text: "Photograph a plant at three different growth stages", hint: null, points: 150 },
    { text: "Photograph a pattern made by nature", hint: "Spirals, fractals, ripples", points: 100 },
    { text: "Photograph an example of camouflage", hint: null, points: 100 },
    { text: "Photograph something that would float and something that would sink", hint: null, points: 100 },
  ],
};

export const HISTORY_DETECTIVE: EduTemplate = {
  templateId: "history-detective",
  title: "History Detective",
  emoji: "🔍",
  description:
    "Hunt for clues about local history hiding in plain sight.",
  subject: "History",
  gradeRange: "3-12",
  durationMinutes: 45,
  challenges: [
    { text: "Photograph a building that looks more than 50 years old", hint: "Brick, stone, or detailed trim are clues", points: 100 },
    { text: "Photograph a historical plaque or marker", hint: null, points: 150 },
    { text: "Photograph a street name with a historical origin", hint: "Founders, wars, geography", points: 100 },
    { text: "Photograph an example of an architectural style", hint: "Colonial, Victorian, modern", points: 100 },
    { text: "Photograph a statue or monument in your community", hint: null, points: 100 },
    { text: "Photograph a tool or object that was once used differently than today", hint: null, points: 100 },
    { text: "Photograph a building that has been repurposed", hint: "An old factory turned into shops or homes", points: 150 },
    { text: "Photograph something that shows how transportation has changed", hint: null, points: 100 },
  ],
};

export const MATH_AROUND_US: EduTemplate = {
  templateId: "math-around-us",
  title: "Math Around Us",
  emoji: "📐",
  description:
    "Find geometric shapes, patterns, and measurements in everyday spaces.",
  subject: "Math",
  gradeRange: "K-8",
  durationMinutes: 45,
  challenges: [
    { text: "Photograph three different geometric shapes you can see", hint: "Circles, triangles, hexagons count", points: 100 },
    { text: "Photograph a pattern that repeats", hint: "Tiles, fences, fabric", points: 100 },
    { text: "Photograph something divided into equal parts", hint: "A pizza slice, a sectioned tray", points: 100 },
    { text: "Photograph an example of symmetry", hint: null, points: 100 },
    { text: "Photograph parallel lines", hint: "Crosswalks, stairs, railings", points: 100 },
    { text: "Photograph a right angle in something man-made", hint: null, points: 100 },
    { text: "Photograph something you would estimate is about one meter long", hint: null, points: 100 },
    { text: "Photograph a number you see in your environment", hint: "House numbers, signs, clocks", points: 100 },
  ],
};

export const ART_SPOTTER: EduTemplate = {
  templateId: "art-spotter",
  title: "Art Spotter",
  emoji: "🎨",
  description:
    "Notice color, composition, and texture in everyday surroundings.",
  subject: "Art",
  gradeRange: "K-8",
  durationMinutes: 45,
  challenges: [
    { text: "Photograph something with complementary colors", hint: "Opposite colors on the wheel: red and green, blue and orange", points: 150 },
    { text: "Photograph a strong texture", hint: "Bark, fabric, brick", points: 100 },
    { text: "Photograph an example of one-point perspective", hint: "A hallway, a road stretching away", points: 150 },
    { text: "Photograph something with bold contrast between light and dark", hint: null, points: 100 },
    { text: "Photograph a composition using the rule of thirds", hint: "Place the subject off-center", points: 150 },
    { text: "Photograph a warm color and a cool color side by side", hint: null, points: 100 },
    { text: "Photograph a reflection", hint: "Window, water, mirror", points: 100 },
    { text: "Photograph a repeating shape that creates a pattern", hint: null, points: 100 },
    { text: "Photograph something that uses negative space well", hint: null, points: 100 },
    { text: "Photograph a piece of public art", hint: "Mural, sculpture, mosaic", points: 100 },
  ],
};

export const KINDNESS_QUEST: EduTemplate = {
  templateId: "kindness-quest",
  title: "Kindness Quest",
  emoji: "💛",
  description:
    "Notice acts of kindness, helpful spaces, and people who care for others.",
  subject: "SEL",
  gradeRange: "K-5",
  durationMinutes: 30,
  challenges: [
    { text: "Photograph a sign that welcomes everyone", hint: null, points: 100 },
    { text: "Photograph someone helping another person", hint: "Ask permission first", points: 150 },
    { text: "Photograph a community helper at work", hint: "Crossing guard, librarian, custodian", points: 100 },
    { text: "Photograph a place that is accessible to everyone", hint: "Ramps, wide doors, braille signs", points: 100 },
    { text: "Photograph something you can do to make someone smile", hint: null, points: 100 },
    { text: "Photograph an example of caring for the environment", hint: "Recycling bin, garden, clean street", points: 100 },
  ],
};

export const TEMPLATES: EduTemplate[] = [
  FIND_SOMEONE_WHO,
  CAMPUS_EXPLORER,
  SCIENCE_IN_THE_WILD,
  HISTORY_DETECTIVE,
  MATH_AROUND_US,
  ART_SPOTTER,
  KINDNESS_QUEST,
];
